import axios from 'axios';

// import Camelize from 'camelcase-keys';
// import {Kebabize} from '@/libs/kebabizeHelper';
import {URL_HOST} from '@/constants/constant';
import HTTPStatusCode from '@/constants/httpStatusCode';
import {getToken} from '@/libs/authStorage';
import apiObserver from '@/observers/apiObserver';
import apiRequestTracker from '@/observers/apiRequestTracker';

// const cacheBuster = () => Math.round(new Date().getTime() / 1000);

const instance = axios.create({
  baseURL: URL_HOST,
  timeout: 2700000, // set timeout to 45 minutes
});

// Request interceptor
instance.interceptors.request.use(
  (config) => {
    const source = axios.CancelToken.source();
    config.cancelToken = source.token;

    // Store cancellation token for each request
    apiRequestTracker.set(config.url, source);

    // JWT
    const token = getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;

    // Add cache-busting parameter to the URL
    // config.params = config.params || {};
    // config.params._cb = cacheBuster();

    // // Add custom header for request type (async or sync)
    const isAsyncRequest = config.asyncRequest || false;
    config.headers['X-Request-Type'] = isAsyncRequest ? 'async' : 'sync';

    console.info('request', `${config.method.toUpperCase()} ${config.url}`);

    // // Check if the request data is of type FormData
    if (config.data instanceof FormData) {
      //   // No need to kebabize FormData, proceed as is
      return config;
    }

    // Convert request data to kebab-case
    // if (config.data) config.data = Kebabize(config.data, {deep: true});

    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor
instance.interceptors.response.use(
  (response) => {
    // Log the request
    console.group('[axios]');
    console.log('requestResponse', `${response.config.method.toUpperCase()} ${response.config.url}`);
    if (response.config.data) console.log('requestData', response.config.data);
    if (response.config.params) console.log('requestParams', response.config.params);
    console.log('requestHeaders', response.config.headers);

    // JWT expired
    if (response?.status === HTTPStatusCode.UNAUTHORIZED) apiObserver.notify.authExpHandler();

    // Check if responseType is 'blob'
    if (response.config.responseType === 'blob') {
      return response; // No need to camelize blob responses
    }

    // Convert response data to camelCase
    // response.data = Camelize(response.data, {deep: true});

    // Log the successful response
    if (response.config.responseType) console.log('responseType', response.config.responseType);
    console.log('responseStatus', response.status);
    console.log('responseData', response.data);
    console.groupEnd();

    apiRequestTracker.delete(response.config.url);

    return response;
  },
  (error) => {
    // Log the error response
    if (axios.isCancel(error)) console.log('Request canceled', error.message);
    else console.error('Request error', error);

    // NOTE: axios.isCancel to handle unnecessary toast during switching page
    if (!error?.config?.isCatchHandled && !axios.isCancel(error)) {
      if (error?.response?.status === HTTPStatusCode.UNAUTHORIZED) {
        apiObserver.notify.authExpHandler();
      } else {
        apiObserver.notify.errorHandler(error, error.response?.config?.url);
      }
    } else {
      console.log('Request error is handled', error?.config?.isCatchHandled);
    }
    console.groupEnd();

    apiRequestTracker.delete(error.response?.config?.url);

    return Promise.reject(error);
  },
);

// Utility functions for HTTP methods
const axiosMethods = {
  get: (url, params, asyncRequest = true, isCatchHandled = false) =>
    instance.get(url, {params, asyncRequest, isCatchHandled}),
  post: (url, data, params, asyncRequest = true, isCatchHandled = false) =>
    instance.post(url, data, {params, asyncRequest, isCatchHandled}),
  put: (url, data, params, asyncRequest = true, isCatchHandled = false) =>
    instance.put(url, data, {params, asyncRequest, isCatchHandled}),
  delete: (url, data, params, asyncRequest = true, isCatchHandled = false) =>
    instance.delete(url, {data, params, asyncRequest, isCatchHandled}),
  postFormData: (url, formData, asyncRequest = true, isCatchHandled = false) =>
    instance.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      asyncRequest,
      isCatchHandled,
    }),
  getBlob: (url, params, asyncRequest = true, isCatchHandled = false) =>
    instance.get(url, {params, responseType: 'blob', asyncRequest, isCatchHandled}),
  postBlob: (url, data, params, asyncRequest = true, isCatchHandled = false) =>
    instance.post(url, data, {params, responseType: 'blob', asyncRequest, isCatchHandled}),
  isCancel: axios.isCancel,
};

export default axiosMethods;

export {instance as axiosInstance};
